import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Banner, Button, Checkbox, Dialog, EmptyState, Facts, Field, Inbox, InboxDetail, Skeleton, StatusBadge, Textarea, TonePill, apiFetch, hasPermission, useApi, useCurrentUser, useT, useToast, type InboxItem } from "@iga/ui";
import type { ApprovalStep, BulkDecisionResult, Campaign, DecisionResult, Page, RequestDetail, RequestListItem } from "../lib/types";
import { CERT_KEYS, REQUEST_KEYS, errMsg, firstName, riskRank, useCampaigns, useFmt, useRelTime, useTasks } from "../lib/hooks";
import { LinkButton, LoadError, PersonLine, PortalRisk, SodNotes } from "../components/common";

type Decision = "approve" | "reject";
const DAY = 86_400_000;

/** A step may be decided in bulk when its item has risk ≤ medium and no SoD violation (same guard as the server). */
const bulkEligible = (s: ApprovalStep) => !!s.item && riskRank[s.item.risk] <= 2 && !(s.item.sodViolations?.length);

/* ------------------------------------------------------------------ approval detail (J2) */

function ApprovalDetail({ step, onDecided }: { step: ApprovalStep; onDecided: () => void }) {
  const t = useT();
  const fmt = useFmt();
  const rel = useRelTime();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useCurrentUser();
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Decision | null>(null);
  const req = useApi<RequestDetail>(["request", step.request.id], `/api/portal/requests/${step.request.id}`);
  useEffect(() => { setComment(""); setError(null); }, [step.stepId]);

  const it = step.item;
  const ben = step.request.beneficiary;
  const requester = step.request.requester;
  const self = requester.id === ben.id;
  const steps = (req.data?.steps ?? []).filter((s) => !it || s.itemId === it.id || s.itemId === null);
  const conflicts = it?.sodViolations ?? [];
  const team = useApi<{ items: { id: string }[] }>(["team"], "/api/portal/team", undefined, { enabled: !!me?.identity && !!me?.isManager, staleTime: 60_000 });
  const isMyReport = !!team.data?.items.some((x) => x.id === ben.id);
  const canLookUp = hasPermission(me?.user.permissions, "identities:read");

  async function decide(d: Decision) {
    if (d === "reject" && !comment.trim()) { setError(t("p.approval.reasonRequired")); document.getElementById(`c-${step.stepId}`)?.focus(); return; }
    setBusy(d); setError(null);
    try {
      await apiFetch<DecisionResult>(`/api/portal/approvals/${step.stepId}/decide`, { method: "POST", json: { decision: d, comment: comment.trim() || undefined } });
      toast.push(d === "approve" ? t("p.approval.approvedToast", { item: it?.displayName ?? "", name: firstName(ben.displayName) }) : t("p.approval.rejectedToast", { item: it?.displayName ?? "", name: firstName(ben.displayName) }), { tone: "ok" });
      for (const k of REQUEST_KEYS) void qc.invalidateQueries({ queryKey: k });
      onDecided();
    } catch (e) { setError(errMsg(e)); toast.push(errMsg(e), { tone: "bad" }); }
    finally { setBusy(null); }
  }

  return (
    <InboxDetail type={t("p.tasks.type.approval")} title={t("p.approval.wants", { name: ben.displayName, item: it?.displayName ?? t("p.approval.request") })}
      status={<span className="flex flex-wrap items-center gap-2"><StatusBadge domain="step" status={step.escalated ? "escalated" : "pending"} audience="portal" params={{ name: t("p.you") }} />{step.dueAt && <span className="text-small text-fg-3">{t(new Date(step.dueAt).getTime() < Date.now() ? "p.tasks.overdue" : "p.tasks.due", { when: rel(step.dueAt) })}</span>}{step.asDelegate && <TonePill tone="neutral">{t("p.approval.asDelegate", { name: step.approverName ?? "" })}</TonePill>}</span>}>
      <div className="flex flex-col gap-4">
        <PersonLine person={ben} secondary={<>{ben.email}{ben.status !== "active" && <> · <StatusBadge domain="identity" status={ben.status} audience="portal" compact /></>}</>} />
        <p className="text-fg-2">{self ? t("p.approval.askedSelf", { name: firstName(ben.displayName), when: fmt.day(step.request.createdAt) }) : t("p.approval.askedBy", { by: requester.displayName, when: fmt.day(step.request.createdAt) })}{it?.system ? ` · ${t("p.approval.in", { system: it.system })}` : ""}</p>
        <Facts labelWidth="140px" items={[
          { label: t("p.approval.lets", { name: firstName(ben.displayName) }), value: <span>{it?.description || t("p.approval.noDescription")}<span className="mt-1 block"><PortalRisk risk={it?.risk} always /></span></span> },
          { label: t("p.approval.why", { name: firstName(requester.displayName) }), value: step.request.justification ? <q className="text-fg">{step.request.justification}</q> : <span className="text-fg-3">{t("p.approval.noJustification")}</span> },
          { label: t("p.approval.until"), value: it?.validTo ? fmt.day(it.validTo) : t("p.approval.noEndDate") },
          { label: t("p.approval.action"), value: t(`p.action.${it?.action ?? "grant"}`) },
        ]} />
        {conflicts.length > 0 && (
          <Banner tone={conflicts.some((c) => c.enforcement === "prevent") ? "bad" : "warn"} title={t("p.approval.conflictTitle", { name: firstName(ben.displayName) })}
            body={<span className="flex flex-col gap-1.5"><SodNotes sod={conflicts} holder={ben.displayName} />{t("p.approval.conflictBody", { name: firstName(ben.displayName) })}</span>} />
        )}
        <div>
          <h3 className="text-h3">{t("p.approval.steps")}</h3>
          {req.isLoading ? <Skeleton rows={2} variant="text" className="mt-2" /> : req.error ? <p className="text-small text-fg-3">{errMsg(req.error)}</p> : (
            <ol className="mt-2 flex flex-col gap-2">
              {steps.map((s) => {
                const mine = s.id === step.stepId;
                const who = mine ? t("p.you") : (s.approverName ?? (s.approverKind === "group" ? t("p.approval.group", { group: s.approverRef ?? "" }) : t("p.approval.unassigned")));
                return (
                  <li key={s.id} className="flex flex-wrap items-center gap-2">
                    <span className="w-14 flex-none text-small text-fg-3">{t("p.approval.step", { n: s.level })}</span>
                    <span className="font-semibold">{who}</span>
                    <span className="text-small text-fg-3">{t(`p.approverKind.${s.approverKind}`)}</span>
                    <StatusBadge domain="step" status={s.state} audience={mine ? "portal" : "admin"} params={{ name: s.approverName ?? who }} compact context={s.state === "skipped" && s.comment === "self_approval_prevented" ? t("p.approval.skippedSelf") : undefined} />
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <Field label={t("p.approval.comment")} hint={t(conflicts.length ? "p.approval.commentHintConflict" : "p.approval.commentHint")} error={error} htmlFor={`c-${step.stepId}`}>
          <Textarea id={`c-${step.stepId}`} value={comment} onChange={(e) => { setComment(e.target.value); if (error) setError(null); }} invalid={!!error} rows={3} />
        </Field>
        <div className="flex flex-wrap items-center gap-2.5">
          <Button size="touch" loading={busy === "approve"} disabled={!!busy} onClick={() => void decide("approve")}>{t("p.approval.approve")}</Button>
          <Button size="touch" variant="secondary" loading={busy === "reject"} disabled={!!busy} onClick={() => void decide("reject")}>{t("p.approval.reject")}</Button>
          {isMyReport ? <LinkButton to={`/team/${ben.id}`} variant="ghost">{t("p.approval.seeAccess", { name: firstName(ben.displayName) })}</LinkButton>
            : canLookUp ? <LinkButton to={`/servicedesk/${ben.id}`} variant="ghost">{t("p.approval.seeAccess", { name: firstName(ben.displayName) })}</LinkButton>
            : <LinkButton to={`/requests/${step.request.id}`} variant="ghost">{t("p.approval.openRequest")}</LinkButton>}
        </div>
      </div>
    </InboxDetail>
  );
}

/* ------------------------------------------------------------------ bulk decision (J2) */

function BulkDialog({ steps, decision, onClose, onDone, onOpen }: { steps: ApprovalStep[]; decision: Decision | null; onClose: () => void; onDone: () => void; onOpen: (stepId: string) => void }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BulkDecisionResult[] | null>(null);
  const open = !!decision && steps.length > 0;
  useEffect(() => { if (!open) { setComment(""); setError(null); setResults(null); } }, [open]);
  async function run() {
    if (!decision) return;
    if (decision === "reject" && !comment.trim()) { setError(t("p.approval.reasonRequired")); return; }
    setBusy(true); setError(null);
    try {
      const r = await apiFetch<{ results: BulkDecisionResult[]; decided: number }>("/api/portal/approvals/decide-bulk", { method: "POST", json: { stepIds: steps.map((s) => s.stepId), decision, comment: comment.trim() || undefined } });
      for (const k of REQUEST_KEYS) void qc.invalidateQueries({ queryKey: k });
      if (r.decided) toast.push(t(decision === "approve" ? "p.bulk.approvedToast" : "p.bulk.rejectedToast", { count: r.decided }), { tone: "ok" });
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length === 0) { onClose(); onDone(); } else { setResults(r.results); onDone(); }
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  }
  const byId = new Map(steps.map((s) => [s.stepId, s]));
  return (
    <Dialog open={open} onClose={onClose} title={t(decision === "approve" ? "p.bulk.approveTitle" : "p.bulk.rejectTitle", { count: steps.length })}
      footer={results ? <Button onClick={onClose}>{t("ui.close")}</Button> : <>
        <Button variant="secondary" onClick={onClose}>{t("ui.cancel")}</Button>
        <Button loading={busy} onClick={() => void run()}>{t(decision === "approve" ? "p.bulk.approveN" : "p.bulk.rejectN", { count: steps.length })}</Button>
      </>}>
      {results ? (
        <div className="flex flex-col gap-3">
          <p className="text-fg-2">{t("p.bulk.someNotDecided")}</p>
          <ul className="flex flex-col gap-2">
            {results.map((r) => { const s = byId.get(r.stepId); return (
              <li key={r.stepId} className="flex flex-wrap items-center justify-between gap-2 rounded-control bg-surface-2 px-3 py-2">
                <span><span className="font-semibold">{s?.item?.displayName}</span> <span className="text-small text-fg-3">{s?.request.beneficiary.displayName}</span></span>
                {r.ok ? <TonePill tone="ok" compact>{t(decision === "approve" ? "p.bulk.approved" : "p.bulk.rejected")}</TonePill>
                  : r.error === "open_individually" ? <Button size="sm" variant="secondary" onClick={() => { onClose(); onOpen(r.stepId); }}>{t("p.bulk.openToDecide")}</Button>
                  : <TonePill tone="bad" compact>{r.error}</TonePill>}
              </li>
            ); })}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="max-h-48 overflow-y-auto text-fg-2">{steps.map((s) => <li key={s.stepId}><span className="font-semibold text-fg">{s.item?.displayName}</span> · {s.request.beneficiary.displayName}</li>)}</ul>
          <Field label={decision === "reject" ? t("p.approval.reason") : t("p.approval.commentOptional")} error={error} htmlFor="bulk-comment">
            <Textarea id="bulk-comment" value={comment} onChange={(e) => { setComment(e.target.value); setError(null); }} invalid={!!error} rows={3} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

/* ------------------------------------------------------------------ the inbox */

export function TasksPage() {
  const t = useT();
  const fmt = useFmt();
  const rel = useRelTime();
  const me = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const tasks = useTasks();
  const approvals = useApi<Page<ApprovalStep>>(["approvals"], "/api/portal/approvals", { page: 1, pageSize: 200 });
  const campaigns = useCampaigns(!!me?.identity);
  const failed = useApi<Page<RequestListItem>>(["my-requests", "failed"], "/api/portal/me/requests", { state: "failed", page: 1, pageSize: 50 }, { enabled: !!me?.identity });
  const source = params.get("source") ?? "all";
  const selectedId = params.get("item");
  const setSelected = useCallback((id: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set("item", id); else n.delete("item"); return n; }, { replace: true }), [setParams]);
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<Decision | null>(null);

  const steps = useMemo(() => approvals.data?.items ?? [], [approvals.data]);
  const stepById = useMemo(() => new Map(steps.map((s) => [s.stepId, s])), [steps]);
  const openCampaigns = useMemo(() => (campaigns.data?.items ?? []).filter((c) => c.pending > 0), [campaigns.data]);
  const failedReqs = useMemo(() => (failed.data?.items ?? []).filter((r) => r.requester.id === me?.identity?.id), [failed.data, me]);

  const items: InboxItem[] = useMemo(() => {
    const out: InboxItem[] = [];
    const now = Date.now();
    if (source === "all" || source === "approvals") {
      for (const s of [...steps].sort((a, b) => (a.dueAt ? new Date(a.dueAt).getTime() : Infinity) - (b.dueAt ? new Date(b.dueAt).getTime() : Infinity))) {
        const due = s.dueAt ? new Date(s.dueAt).getTime() : null;
        const group = s.escalated ? "blocked" : due !== null && due <= now + DAY ? "attention" : "later";
        const flags = [s.item && riskRank[s.item.risk] >= 3 ? t(`risk.${riskRank[s.item.risk]}`) : null, s.item?.sodViolations?.length ? t("status.sod.open.portal") : null].filter(Boolean).join(" · ");
        const eligible = bulkEligible(s);
        const title = <span className="flex items-center gap-2">{selectMode && (eligible
          ? <Checkbox aria-label={t("p.bulk.select", { item: `${s.item?.displayName ?? ""} · ${s.request.beneficiary.displayName}` })} checked={picked.has(s.stepId)} onClick={(e) => e.stopPropagation()} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(s.stepId); else n.delete(s.stepId); return n; })} />
          : <span className="text-small font-medium text-fg-3">{t("p.bulk.openToDecide")}</span>)}{s.item?.displayName ?? t("p.approval.request")}</span>;
        out.push({ id: `a:${s.stepId}`, group, type: t("p.tasks.type.approval"), title, tone: s.escalated ? "bad" : group === "attention" ? "warn" : "info",
          context: `${t("p.tasks.for", { name: s.request.beneficiary.displayName })} · ${s.request.requester.id === s.request.beneficiary.id ? t("p.tasks.askedSelf") : t("p.tasks.askedBy", { name: s.request.requester.displayName })}${flags ? ` · ${flags}` : ""}`,
          age: s.dueAt ? (due! < now ? t("p.tasks.overdueShort") : t("p.tasks.dueShort", { when: fmt.day(s.dueAt) })) : undefined });
      }
    }
    if (source === "all" || source === "reviews") {
      for (const c of openCampaigns) out.push({ id: `c:${c.id}`, group: c.overdue ? "attention" : "later", type: t("p.tasks.type.review"), title: c.name, tone: c.overdue ? "warn" : "info", context: t("p.tasks.reviewContext", { left: c.pending, total: c.total }), age: c.overdue ? t("p.tasks.overdueShort") : t("p.tasks.dueShort", { when: fmt.day(c.dueAt) }) });
    }
    if (source === "all" || source === "requests") {
      for (const r of failedReqs) out.push({ id: `r:${r.id}`, group: "blocked", type: t("p.tasks.type.request"), title: (r.items ?? []).map((i) => i.displayName).filter(Boolean).join(", ") || t("p.requests.number", { n: r.number }), tone: "bad", context: `${t("p.tasks.for", { name: r.beneficiary.displayName })} · ${t("status.request.failed.portal")}`, age: rel(r.createdAt) });
    }
    return out;
  }, [steps, openCampaigns, failedReqs, source, selectMode, picked, t, fmt, rel]);

  const ordered = useMemo(() => (["blocked", "attention", "later"] as const).flatMap((g) => items.filter((i) => i.group === g)), [items]);
  const selected = ordered.find((i) => i.id === selectedId) ?? null;
  const nextAfter = (id: string) => { const idx = ordered.findIndex((i) => i.id === id); return ordered[idx + 1]?.id ?? ordered[idx - 1]?.id ?? null; };
  const refresh = () => { setPicked(new Set()); for (const k of [...REQUEST_KEYS, ...CERT_KEYS]) void qc.invalidateQueries({ queryKey: k }); };

  const loading = approvals.isLoading || (!!me?.identity && (campaigns.isLoading || failed.isLoading));
  const total = steps.length + openCampaigns.length + failedReqs.length;
  const tk = tasks.data;
  const urgent = (tk?.blocked ?? 0) + (tk?.needsAttention ?? 0);
  const subtitle = total === 0 ? t("p.home.nothingWaiting") : urgent > 0 ? t("p.tasks.waitUrgent", { count: total, urgent }) : total === 1 ? t("p.tasks.waitOne") : t("p.tasks.wait", { count: total });
  const pickedSteps = [...picked].map((id) => stepById.get(id)).filter((s): s is ApprovalStep => !!s && bulkEligible(s));
  const eligibleCount = steps.filter(bulkEligible).length;

  const sources = [
    { id: "all", label: t("ui.all"), count: steps.length + openCampaigns.length + failedReqs.length },
    { id: "approvals", label: t("p.tasks.source.approvals"), count: steps.length },
    ...(me?.identity ? [{ id: "reviews", label: t("p.tasks.source.reviews"), count: openCampaigns.length }, { id: "requests", label: t("p.tasks.source.requests"), count: failedReqs.length }] : []),
  ];

  function renderDetail(item: InboxItem) {
    const [kind, id] = [item.id.slice(0, 1), item.id.slice(2)];
    if (kind === "a") {
      const s = stepById.get(id);
      if (!s) return null;
      return <ApprovalDetail step={s} onDecided={() => { const n = nextAfter(item.id); setSelected(n); }} />;
    }
    if (kind === "c") {
      const c = openCampaigns.find((x) => x.id === id) as Campaign | undefined;
      if (!c) return null;
      return (
        <InboxDetail type={t("p.tasks.type.review")} title={c.name} status={<span className="flex flex-wrap items-center gap-2"><TonePill tone={c.overdue ? "warn" : "info"}>{c.overdue ? t("p.tasks.overdueShort") : t("p.tasks.dueShort", { when: fmt.day(c.dueAt) })}</TonePill><span className="text-small text-fg-3">{t("p.review.progress", { decided: c.decided, total: c.total })}</span></span>}
          actions={<LinkButton to={`/review/${c.id}`} variant="primary">{t("p.tasks.openReview")}</LinkButton>}>
          <p>{t("p.review.purpose")}</p>
          <p className="mt-2">{t("p.tasks.reviewLeft", { left: c.pending, date: fmt.day(c.dueAt) })}</p>
        </InboxDetail>
      );
    }
    const r = failedReqs.find((x) => x.id === id);
    if (!r) return null;
    return (
      <InboxDetail type={t("p.tasks.type.request")} title={t("p.tasks.failedTitle", { n: r.number })} status={<StatusBadge domain="request" status="failed" audience="portal" />}
        fix={t("p.tasks.failedFix")} actions={<LinkButton to={`/requests/${r.id}`} variant="primary">{t("p.approval.openRequest")}</LinkButton>}>
        <p>{t("p.tasks.failedBody", { name: r.beneficiary.displayName })}</p>
        <ul className="mt-2 list-disc pl-5">{(r.items ?? []).map((i) => <li key={i.id}>{i.displayName} <StatusBadge domain="request" status={i.state === "failed" ? "failed" : i.state === "completed" ? "completed" : i.state} audience="portal" compact /></li>)}</ul>
      </InboxDetail>
    );
  }

  const error = approvals.error ?? campaigns.error ?? failed.error;
  return (
    <div className="flex min-h-[70vh] flex-col gap-4">
      <LoadError error={error} onRetry={() => { void approvals.refetch(); void campaigns.refetch(); void failed.refetch(); }} />
      {loading ? <><h1 className="text-h1-portal">{t("p.nav.tasks")}</h1><div className="rounded-card border border-line bg-surface p-4"><Skeleton rows={4} /></div></> : (
        <Inbox heading={t("p.nav.tasks")} items={items} selectedId={selectedId} onSelect={setSelected} renderDetail={renderDetail}
          subheading={<>
            <span>{subtitle}</span>
            {eligibleCount > 0 && !selectMode && <> · <button type="button" className="font-semibold text-brand-text hover:underline" onClick={() => setSelectMode(true)}>{t("p.bulk.selectMode")}</button></>}
            {selectMode && (
              <span className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-semibold text-fg">{t("p.bulk.selected", { count: pickedSteps.length })}</span>
                <span className="text-small">{t("p.bulk.guard")}</span>
                <Button size="sm" disabled={pickedSteps.length === 0} onClick={() => setBulk("approve")}>{t("p.bulk.approveN", { count: pickedSteps.length })}</Button>
                <Button size="sm" variant="secondary" disabled={pickedSteps.length === 0} onClick={() => setBulk("reject")}>{t("p.bulk.rejectN", { count: pickedSteps.length })}</Button>
                <Button size="sm" variant="ghost" onClick={() => { setSelectMode(false); setPicked(new Set()); }}>{t("ui.cancel")}</Button>
              </span>
            )}
          </>}
          sources={{ items: sources, value: source, onChange: (id) => setParams((p) => { const n = new URLSearchParams(p); n.set("source", id); n.delete("item"); return n; }, { replace: true }) }}
          healthy={total > 0 && urgent === 0 ? t("p.tasks.healthy") : undefined}
          emptyState={<EmptyState kind="all-done" title={t("p.home.nothingWaiting")} body={t("p.tasks.emptyBody")} />} />
      )}
      {selected === null && selectedId && !loading && <p className="sr-only" role="status">{t("p.tasks.itemGone")}</p>}
      <BulkDialog steps={pickedSteps} decision={bulk} onClose={() => setBulk(null)} onDone={refresh} onOpen={(stepId) => { setSelectMode(false); setSelected(`a:${stepId}`); }} />
    </div>
  );
}
